import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { SelectChangeEvent } from "@mui/material";
import { useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
	changeCategoryDataElem,
	changeCategoryDataEndpoint,
	changeCategoryDataEndpointEntry,
	changeCategoryDataException,
	changeTypeCategoryDataElem,
	changeTypeCategoryEndpoint,
	changeTypeCategoryEndpointEntry,
	changeTypeCategoryException,
	changeTypeConditionEntryEndpoint,
	changeTypeConditionRunActionDataElem,
	changeTypeConditionRunActionEndpoint,
	changeTypeConditionRunActionEndpointEntry,
	changeTypeConditionRunActionException,
	changeTypeConditionRunEndpoint,
	changeTypeConditionRunInEntryEndpoint,
	changeTypeConditionSlaveDataDataElem,
	changeValuesConditionRunActionDataElem,
	changeValuesConditionRunActionEndpoint,
	changeValuesConditionRunActionEndpointEntry,
	changeValuesConditionRunActionException,
	changeValuesConditionSlaveDataDataElem,
	deleteCategoryDataElem,
	deleteCategoryDataEndpoint,
	deleteCategoryDataEndpointEntry,
	deleteCategoryDataException,
	deleteConditionEntry,
	deleteConditionRun,
	deleteConditionRunActionDataElem,
	deleteConditionRunActionEndpoint,
	deleteConditionRunActionEndpointEntry,
	deleteConditionRunActionException,
	deleteConditionRunInEntry,
	deleteConditionSlaveDataDataElem,
	editConditionEntry,
	editConditionRun,
	editConditionRunInEntry,
	selectEndpointElem,
} from "@/store/ses/sesItemConfigSlice";
import {
	changeCategoryDataExceptionRobot,
	changeCategoryDataRobot,
	changeTypeCategoryExceptionRobot,
	changeTypeCategoryRobot,
	changeTypeConditionRunActionExceptionRobot,
	changeTypeConditionRunActionRobot,
	changeValuesConditionRunActionExceptionRobot,
	changeValuesConditionRunActionRobot,
	deleteCategoryDataExceptionRobot,
	deleteCategoryDataRobot,
	deleteConditionRunActionExceptionRobot,
	deleteConditionRunActionRobot,
	selectRobot,
} from "@/store/ses/sesRobotSlice";
import { selectDataList } from "@/store/ses/sesDataElemSlice";
import { selectEndpointList } from "@/store/ses/sesEndpointSlice";
import { IConditionProps } from "./Condition.props";
import {
	AtomicCondition,
	ConditionType,
	DataElemActionEventType,
	DepthType,
	IConditionConfirmation,
	IConditionData,
	IConditionInterval,
	IConditionVariable,
	OperationType,
} from "@/types/ses/sesItemConfigTypes";
import { YesNoType } from "@/types/qas/qasTypes";
import { ExceptionType, RobotActionEventType } from "@/types/ses/sesRobotTypes";
import { useTranslate } from "@/hooks";
import { convertSearchDepth } from "@/helpers";
import { CONDITIONS_LIST } from "./Condition.const";

export const useCondition = (props: IConditionProps) => {
	const {
		conditionData,
		conditionBlockIdx,
		conditionIdx,
		conditionFor,
		changeFlgHandler,
		orBlockIndex,
		dataElemListOptions,
		conditionWithOrOption,
		setIsConditionReady,
	} = props;

	const isInitialMount = useRef(true);
	const dispatch = useAppDispatch();
	const translate = useTranslate(); // hook для перевода текста
	const robotInfo = useAppSelector(selectRobot); // store - информация о роботе
	const dataElemList = useAppSelector(selectDataList); // список КТ
	const endpointList = useAppSelector(selectEndpointList); // список КТ
	const endpointElem = useAppSelector(selectEndpointElem); // store - КТ
	const [selectConditionType, setSelectConditionType] = useState<Exclude<ConditionType, "or">>(conditionData.type); // тип условия запуска
	const [extendedselectConditionType, setExtendedSelectConditionType] = useState<Exclude<ConditionType, "or"> | YesNoType>(
		conditionData.type !== "confirmation" ? conditionData.type : conditionData.value
	); // тип условия запуска

	const [selectDepth, setSelectDepth] = useState<DepthType>(
		conditionData.type === "data" ? (typeof conditionData.depth === "number" ? convertSearchDepth(conditionData.depth) : conditionData.depth) : "endpoint"
	); // глубина поиска
	const [selectEventId, setSelectEventId] = useState<RobotActionEventType>(conditionData.type === "event" ? conditionData.id : "start");
	// const [inputDataElem, setInputDataElem] = useState<IDataElemItem | null>(null); // элемент данных для поля id

	const [selectOperation, setSelectOperation] = useState<OperationType>(
		conditionData.type === "data" || conditionData.type === "variable" ? conditionData.operation : "exists"
	); // операция
	const [confirmationValue, setConfirmationValue] = useState<YesNoType>(conditionData.type === "confirmation" ? conditionData.value : "no"); // значение для подтверждения
	const [checkIntervalEquality, setCheckIntervalEquality] = useState<boolean>(
		conditionData.type === "interval" && typeof conditionData.equal === "boolean" ? !conditionData.equal : false
	); // флаг равества интервала (инвертированный)
	const initialValuesRef = useRef<{
		selectDepth: DepthType;
		selectOperation: OperationType;
		inputDataElem: string;
		selectedId: string;
		inputValue: string;
		checkIntervalEquality: boolean;
	} | null>(null);

	const formik = useFormik({
		initialValues: {
			inputDataElem: "",
			selectedId: conditionData.type !== "break" && conditionData.type !== "confirmation" ? conditionData.id : "",
			inputValue: conditionData.type === "data" || conditionData.type === "variable" ? conditionData.value : "",
		},
		validateOnMount: false,
		validateOnChange: false,
		validateOnBlur: true,
		onSubmit: () => {},
	});
	const dataElemOptions = useMemo(
		() => {
			if (!dataElemListOptions) return [];
			return conditionFor.for === "dataElemSlaveData"
				? dataElemListOptions.dataElemSlaveData.map((el) => ({ value: el.id, label: el.name }))
				: dataElemListOptions.dataElem.map((el) => ({ value: el.id, label: el.name }));
		},
		[dataElemListOptions, conditionFor.for] // ссылки меняются только при реальном изменении
	);

	const timeIntervalsOptions = useMemo(() => Object.keys(robotInfo.data?.timeintervals || {}).map((el) => ({ value: el, label: el })), [robotInfo.data?.timeintervals]);

	const variableNamesOptions = useMemo(() => endpointList.variableNames.map((el) => ({ value: el, label: el })), [endpointList.variableNames]);

	const variableValuesOptions = useMemo(() => endpointList.variableValues.map((el) => ({ value: el, label: el })), [endpointList.variableValues]);

	const handleChangeInputDataElem = useCallback((value: string) => formik.setFieldValue("inputDataElem", value), [formik.setFieldValue]);

	const handleChangeInputValue = useCallback((value: string) => formik.setFieldValue("inputValue", value), [formik.setFieldValue]);

	const conditionsTypesList = useMemo(
		() =>
			CONDITIONS_LIST.filter(
				(el) =>
					!(el.value === "no" && (conditionFor.for === "categoryAction" || conditionFor.for === "endpointEntry" || conditionFor.for === "dataElemSlaveData")) &&
					!(el.value === "yes" && (conditionFor.for === "categoryAction" || conditionFor.for === "endpointEntry" || conditionFor.for === "dataElemSlaveData")) &&
					!(el.value === "interval" && (conditionFor.for === "categoryAction" || conditionFor.for === "endpointRun")) &&
					!(el.value === "or" && !conditionWithOrOption)
			),
		[conditionFor.for, conditionWithOrOption]
	);

	const changeCheckIntervalEquality = (e: ChangeEvent<HTMLInputElement>) => {
		setCheckIntervalEquality(e.target.checked);
	};

	// обработчик изменения типа условия запуска
	const changeTypeConditionRunHandler = (e: SelectChangeEvent<ConditionType | "yes" | "no">): void => {
		const value = e.target.value as ConditionType | YesNoType;
		if (value !== "or") {
			setExtendedSelectConditionType(e.target.value as Exclude<ConditionType, "or"> | YesNoType);
		}

		if (value === "yes" || value === "no") {
			setSelectConditionType("confirmation");
			setTimeout(() => {
				setConfirmationValue(value);
			}, 100);
		} else {
			if (value !== "or") {
				setExtendedSelectConditionType(value);
				setSelectConditionType(value);
			}
		}

		if (conditionFor.for === "action") {
			const data = {
				channel: conditionFor.channel,
				channelIdx: conditionFor.channelIdx,
				conditionBlockIdx,
				conditionIdx,
				conditionType: value === "yes" || value === "no" ? "confirmation" : value,
				confirmationValue: value === "yes" || value === "no" ? value : undefined,
			};

			if (conditionFor.itsAction === "robotException") {
				dispatch(changeTypeConditionRunActionExceptionRobot({ exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "dataException") {
				dispatch(
					changeTypeConditionRunActionException({
						targetObject: "dataElement",
						exception: conditionFor.actionEvent as ExceptionType,
						orBlockIndex: orBlockIndex,
						...data,
					})
				);
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointException") {
				dispatch(
					changeTypeConditionRunActionException({ targetObject: "endpoint", exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }) //+
				);
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointEntry") {
				dispatch(changeTypeConditionRunActionEndpointEntry({ orBlockIndex: orBlockIndex, ...data })); // +
				changeFlgHandler("entry"); // ставим флаг о несохраненных данных
			} else {
				conditionFor.itsAction === "robotEvent" &&
					dispatch(changeTypeConditionRunActionRobot({ actionEvent: conditionFor.actionEvent as RobotActionEventType, orBlockIndex: orBlockIndex, ...data })); //+
				conditionFor.itsAction === "dataElement" &&
					dispatch(changeTypeConditionRunActionDataElem({ actionEvent: conditionFor.actionEvent as DataElemActionEventType, orBlockIndex: orBlockIndex, ...data })); //+
				conditionFor.itsAction === "endpoint" && dispatch(changeTypeConditionRunActionEndpoint({ orBlockIndex: orBlockIndex, ...data })); //+
				changeFlgHandler("actions"); // ставим флаг о несохраненных данных
			}
		} else if (conditionFor.for === "categoryAction") {
			const data = {
				channel: conditionFor.channel,
				channelIdx: conditionFor.channelIdx,
				categoryId: conditionFor.categoryId,
				categoryBlockIdx: conditionBlockIdx,
				categoryIdx: conditionIdx,
				conditionType: e.target.value as Extract<ConditionType, "data" | "variable">,
				confirmationValue: value === "yes" || value === "no" ? value : undefined,
			};
			if (conditionFor.itsAction === "robotException") {
				dispatch(changeTypeCategoryExceptionRobot({ exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "dataException") {
				dispatch(changeTypeCategoryException({ targetObject: "dataElement", exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointException") {
				dispatch(changeTypeCategoryException({ targetObject: "endpoint", exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointEntry") {
				dispatch(changeTypeCategoryEndpointEntry({ orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("entry"); // ставим флаг о несохраненных данных
			} else {
				conditionFor.itsAction === "robotEvent" &&
					dispatch(changeTypeCategoryRobot({ actionEvent: conditionFor.actionEvent as RobotActionEventType, orBlockIndex: orBlockIndex, ...data }));
				conditionFor.itsAction === "dataElement" &&
					dispatch(changeTypeCategoryDataElem({ actionEvent: conditionFor.actionEvent as DataElemActionEventType, orBlockIndex: orBlockIndex, ...data }));
				conditionFor.itsAction === "endpoint" && dispatch(changeTypeCategoryEndpoint({ orBlockIndex: orBlockIndex, ...data })); //+
				changeFlgHandler("actions"); // ставим флаг о несохраненных данных
			}
		} else if (conditionFor.for === "endpointRun") {
			dispatch(
				changeTypeConditionRunEndpoint({
					conditionBlockIdx,
					conditionIdx,
					conditionType: value === "yes" || value === "no" ? "confirmation" : value,
					confirmationValue: value === "yes" || value === "no" ? value : undefined,
					orBlockIndex,
				})
			);
			changeFlgHandler("run"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "endpointEntry") {
			dispatch(
				changeTypeConditionEntryEndpoint({
					conditionBlockIdx,
					conditionIdx,
					conditionType: value === "yes" || value === "no" ? "confirmation" : (value as Extract<ConditionType, "data" | "variable">),
					confirmationValue: value === "yes" || value === "no" ? value : undefined,
					orBlockIndex,
				})
			);
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "endpointRunInEntry") {
			dispatch(
				changeTypeConditionRunInEntryEndpoint({
					conditionEntryBlockIdx: conditionFor.conditionEntryBlockIdx,
					conditionRunBlockIdx: conditionBlockIdx,
					conditionIdx,
					conditionType: value === "yes" || value === "no" ? "confirmation" : (value as Extract<ConditionType, "data" | "variable">),
					confirmationValue: value === "yes" || value === "no" ? value : undefined,
					orBlockIndex,
				})
			);
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "dataElemSlaveData") {
			dispatch(
				changeTypeConditionSlaveDataDataElem({
					conditionBlockIdx,
					conditionIdx,
					conditionType: value === "yes" || value === "no" ? "confirmation" : value,
					confirmationValue: value === "yes" || value === "no" ? value : undefined,
					orBlockIndex,
				}) //
			);
			changeFlgHandler("slaveData"); // ставим флаг о несохраненных данных
		}
	};

	const handleFormatSeparator = useCallback(
		(arg: string): string =>
			arg
				.split(/[,|]/)
				.map((el) => el.trim())
				.join("|"),
		[]
	);

	const creatingObjectWithCondition = useCallback(
		(type: Exclude<ConditionType, "or">): AtomicCondition => {
			switch (type) {
				case "data":
					return {
						type,
						depth: selectDepth,
						id: formik.values.inputDataElem || "",
						operation: selectOperation,
						value: handleFormatSeparator(formik.values.inputValue),
					};
				case "variable":
					return {
						type,
						id: formik.values.selectedId,
						operation: selectOperation,
						value: handleFormatSeparator(formik.values.inputValue),
					};
				case "interval":
					return {
						type,
						equal: !checkIntervalEquality, // инвертированный
						id: formik.values.selectedId,
					};
				case "confirmation":
					return {
						type,
						value: extendedselectConditionType as "yes" | "no",
					};
				case "break":
					return {
						type,
					};
				case "event":
					return {
						type,
						id: selectEventId,
					};
				default:
					const defaultType: never = type;
					return defaultType;
			}
		},
		[
			selectDepth,
			selectOperation,
			formik.values.inputDataElem,
			formik.values.inputValue,
			formik.values.selectedId,
			extendedselectConditionType,
			checkIntervalEquality,
			selectEventId,
		]
	);

	// обработчик изменения значений условия запуска
	const changeValuesConditionRunHandler = (): void => {
		if (conditionFor.for === "action") {
			const data = {
				channel: conditionFor.channel,
				channelIdx: conditionFor.channelIdx,
				conditionBlockIdx,
				conditionIdx,
				data: creatingObjectWithCondition(selectConditionType),
			};

			if (conditionFor.itsAction === "robotException") {
				dispatch(changeValuesConditionRunActionExceptionRobot({ exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных +++
			} else if (conditionFor.itsAction === "dataException") {
				dispatch(
					changeValuesConditionRunActionException({
						targetObject: "dataElement",
						orBlockIndex: orBlockIndex,
						exception: conditionFor.actionEvent as ExceptionType,
						...data,
					}) // ++
				);
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointException") {
				dispatch(
					changeValuesConditionRunActionException({ targetObject: "endpoint", orBlockIndex: orBlockIndex, exception: conditionFor.actionEvent as ExceptionType, ...data })
				); // ++
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointEntry") {
				dispatch(changeValuesConditionRunActionEndpointEntry({ orBlockIndex: orBlockIndex, ...data })); //+++
				changeFlgHandler("entry"); // ставим флаг о несохраненных данных
			} else {
				conditionFor.itsAction === "robotEvent" &&
					dispatch(changeValuesConditionRunActionRobot({ actionEvent: conditionFor.actionEvent as RobotActionEventType, orBlockIndex: orBlockIndex, ...data })); //+++
				conditionFor.itsAction === "dataElement" &&
					dispatch(changeValuesConditionRunActionDataElem({ actionEvent: conditionFor.actionEvent as DataElemActionEventType, orBlockIndex: orBlockIndex, ...data })); //+++
				conditionFor.itsAction === "endpoint" && dispatch(changeValuesConditionRunActionEndpoint({ orBlockIndex: orBlockIndex, ...data })); //+++
				changeFlgHandler("actions"); // ставим флаг о несохраненных данных
			}
		} else if (conditionFor.for === "categoryAction") {
			const data = {
				channel: conditionFor.channel,
				channelIdx: conditionFor.channelIdx,
				categoryId: conditionFor.categoryId,
				categoryBlockIdx: conditionBlockIdx,
				categoryIdx: conditionIdx,
				data: creatingObjectWithCondition(conditionData.type) as IConditionData,
			};

			if (conditionFor.itsAction === "robotException") {
				dispatch(changeCategoryDataExceptionRobot({ exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "dataException") {
				dispatch(changeCategoryDataException({ targetObject: "dataElement", exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointException") {
				dispatch(changeCategoryDataException({ targetObject: "endpoint", exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointEntry") {
				dispatch(changeCategoryDataEndpointEntry({ orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("entry"); // ставим флаг о несохраненных данных
			} else {
				conditionFor.itsAction === "robotEvent" &&
					dispatch(changeCategoryDataRobot({ actionEvent: conditionFor.actionEvent as RobotActionEventType, orBlockIndex: orBlockIndex, ...data }));
				conditionFor.itsAction === "dataElement" &&
					dispatch(changeCategoryDataElem({ actionEvent: conditionFor.actionEvent as DataElemActionEventType, orBlockIndex: orBlockIndex, ...data }));
				conditionFor.itsAction === "endpoint" && dispatch(changeCategoryDataEndpoint({ orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("actions"); // ставим флаг о несохраненных данных
			}
		} else if (conditionFor.for === "endpointRun") {
			dispatch(
				editConditionRun({
					conditionBlockIdx,
					conditionIdx,
					data: creatingObjectWithCondition(conditionData.type) as IConditionData,
					orBlockIndex,
				})
			);
			changeFlgHandler("run"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "endpointEntry") {
			dispatch(
				editConditionEntry({
					conditionBlockIdx,
					conditionIdx,
					data: creatingObjectWithCondition(conditionData.type) as IConditionData | IConditionVariable | IConditionConfirmation,
					orBlockIndex,
				})
			);
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "endpointRunInEntry") {
			dispatch(
				editConditionRunInEntry({
					conditionEntryBlockIdx: conditionFor.conditionEntryBlockIdx,
					conditionRunBlockIdx: conditionBlockIdx,
					conditionIdx,
					data: creatingObjectWithCondition(conditionData.type) as IConditionData | IConditionVariable | IConditionConfirmation | IConditionInterval,
					orBlockIndex,
				})
			);
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "dataElemSlaveData") {
			dispatch(
				changeValuesConditionSlaveDataDataElem({
					conditionBlockIdx,
					conditionIdx,
					data: creatingObjectWithCondition(conditionData.type),
					orBlockIndex,
				})
			);
			changeFlgHandler("slaveData"); // ставим флаг о несохраненных данных
		}
	};

	// обработчик удаления условия
	const deleteConditionHandler = (): void => {
		if (conditionFor.for === "action") {
			const data = {
				channel: conditionFor.channel,
				channelIdx: conditionFor.channelIdx,
				conditionBlockIdx,
				conditionIdx,
			};
			if (conditionFor.itsAction === "robotException") {
				dispatch(deleteConditionRunActionExceptionRobot({ exception: conditionFor.actionEvent as ExceptionType, orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "dataException") {
				dispatch(
					deleteConditionRunActionException({ targetObject: "dataElement", orBlockIndex: orBlockIndex, exception: conditionFor.actionEvent as ExceptionType, ...data })
				);
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointException") {
				dispatch(
					deleteConditionRunActionException({ targetObject: "endpoint", orBlockIndex: orBlockIndex, exception: conditionFor.actionEvent as ExceptionType, ...data })
				);
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointEntry") {
				dispatch(deleteConditionRunActionEndpointEntry({ orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("entry"); // ставим флаг о несохраненных данных
			} else {
				conditionFor.itsAction === "robotEvent" &&
					dispatch(deleteConditionRunActionRobot({ actionEvent: conditionFor.actionEvent as RobotActionEventType, orBlockIndex: orBlockIndex, ...data }));
				conditionFor.itsAction === "dataElement" &&
					dispatch(deleteConditionRunActionDataElem({ actionEvent: conditionFor.actionEvent as DataElemActionEventType, orBlockIndex: orBlockIndex, ...data }));
				conditionFor.itsAction === "endpoint" && dispatch(deleteConditionRunActionEndpoint({ orBlockIndex: orBlockIndex, ...data }));
				changeFlgHandler("actions"); // ставим флаг о несохраненных данных
			}
		} else if (conditionFor.for === "categoryAction") {
			const data = {
				channel: conditionFor.channel,
				channelIdx: conditionFor.channelIdx,
				categoryId: conditionFor.categoryId,
				categoryBlockIdx: conditionBlockIdx,
				categoryIdx: conditionIdx,
			};
			if (conditionFor.itsAction === "robotException") {
				dispatch(deleteCategoryDataExceptionRobot({ exception: conditionFor.actionEvent as ExceptionType, orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "dataException") {
				dispatch(deleteCategoryDataException({ targetObject: "dataElement", exception: conditionFor.actionEvent as ExceptionType, orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointException") {
				dispatch(deleteCategoryDataException({ targetObject: "endpoint", exception: conditionFor.actionEvent as ExceptionType, orBlockIndex, ...data }));
				changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
			} else if (conditionFor.itsAction === "endpointEntry") {
				dispatch(deleteCategoryDataEndpointEntry({ orBlockIndex, ...data }));
				changeFlgHandler("entry"); // ставим флаг о несохраненных данных
			} else {
				conditionFor.itsAction === "robotEvent" &&
					dispatch(deleteCategoryDataRobot({ actionEvent: conditionFor.actionEvent as RobotActionEventType, orBlockIndex, ...data }));
				conditionFor.itsAction === "dataElement" &&
					dispatch(deleteCategoryDataElem({ actionEvent: conditionFor.actionEvent as DataElemActionEventType, orBlockIndex, ...data }));
				conditionFor.itsAction === "endpoint" && dispatch(deleteCategoryDataEndpoint({ orBlockIndex, ...data }));
				changeFlgHandler("actions"); // ставим флаг о несохраненных данных
			}
		} else if (conditionFor.for === "endpointRun") {
			dispatch(
				deleteConditionRun({
					conditionBlockIdx,
					conditionIdx,
					orBlockIndex,
				})
			);
			changeFlgHandler("run"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "endpointEntry") {
			dispatch(
				deleteConditionEntry({
					conditionBlockIdx,
					conditionIdx,
					orBlockIndex,
				})
			);
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "endpointRunInEntry") {
			dispatch(
				deleteConditionRunInEntry({
					conditionEntryBlockIdx: conditionFor.conditionEntryBlockIdx,
					conditionRunBlockIdx: conditionBlockIdx,
					conditionIdx,
					orBlockIndex,
				})
			);
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else if (conditionFor.for === "dataElemSlaveData") {
			dispatch(
				deleteConditionSlaveDataDataElem({
					conditionBlockIdx,
					conditionIdx,
					orBlockIndex,
				})
			);
			changeFlgHandler("slaveData"); // ставим флаг о несохраненных данных
		}
	};

	const handleChangeSelectDepth = (e: SelectChangeEvent<DepthType>) => {
		const newDepth = e.target.value as DepthType;
		setSelectDepth(newDepth);
	};

	const handleChangeSelectOperation = (e: SelectChangeEvent<OperationType>) => {
		const newOperation = e.target.value as OperationType;
		setSelectOperation(newOperation);
		if (newOperation === "not_exists" || newOperation === "exists") {
			handleChangeInputValue("");
		}
	};

	useLayoutEffect(() => {
		let timer: NodeJS.Timeout;

		if (conditionData.type === "data") {
			if (dataElemListOptions !== null) {
				timer = setTimeout(() => {
					setIsConditionReady(true);
					// Устанавливаем флаг, что инициализация завершена
					isInitialMount.current = false;
				});
			}
		} else {
			timer = setTimeout(() => {
				setIsConditionReady(true);
				// Устанавливаем флаг, что инициализация завершена
				isInitialMount.current = false;
			});
		}

		return () => clearTimeout(timer);
	}, [conditionData.type, formik.values.inputDataElem]);

	// следим за условием
	useEffect(() => {
		if (conditionData.type === "data") {
			const dataElementFound = dataElemList.data.find((dataElemItem) => dataElemItem.id === conditionData.id);

			const newValue =
				dataElementFound ||
				(conditionData.id === ""
					? null
					: {
							id: conditionData.id,
							name: `${conditionData.id} (${translate("title_notFound")})`,
							type: "smc",
							rtype: "report",
							subtype: "class",
							weight: 1,
							hasNearestAction: false,
							hasScript: false,
							model: "",
							param: "",
					  });

			// Обновляем только если значение действительно изменилось
			if ((!formik.values.inputDataElem && newValue) || (formik.values.inputDataElem && !newValue) || (newValue && formik.values.inputDataElem !== newValue.id)) {
				handleChangeInputDataElem(conditionData.id);
			}
		}
	}, [conditionData, dataElemList.data]);

	useEffect(() => {
		if (conditionData.type !== "data") return;

		formik.setFieldValue("inputDataElem", conditionData.id);
	}, [formik.values.selectedId]);

	useEffect(() => {
		if (conditionData.type === "data" && formik.values.inputDataElem === conditionData.id) {
			initialValuesRef.current = {
				selectDepth,
				selectOperation,
				inputDataElem: formik.values.inputDataElem,
				selectedId: formik.values.selectedId,
				inputValue: formik.values.inputValue,
				checkIntervalEquality,
			};
		}
		//@ts-ignore
	}, [formik.values.inputDataElem, conditionData.id]);

	useEffect(() => {
		if (selectOperation === "exists" || selectOperation === "not_exists") {
			handleChangeInputValue("");
		}
	}, [selectOperation]);

	useEffect(() => {
		if (!isInitialMount.current) {
			changeValuesConditionRunHandler();
		}
	}, [checkIntervalEquality]);

	useEffect(() => {
		if (!initialValuesRef.current) return;

		const current = {
			selectDepth,
			selectOperation,
			inputDataElem: formik.values.inputDataElem,
			selectedId: formik.values.selectedId,
			inputValue: formik.values.inputValue,
			checkIntervalEquality,
		};

		const isChanged = JSON.stringify(current) !== JSON.stringify(initialValuesRef.current);

		if (isChanged) {
			changeValuesConditionRunHandler();
		}
	}, [selectDepth, selectOperation, formik.values.inputDataElem, formik.values.selectedId, formik.values.inputValue, checkIntervalEquality]);

	return {
		// STATE
		selectConditionType,
		extendedselectConditionType,
		selectDepth,
		selectOperation,
		selectEventId,
		confirmationValue,
		checkIntervalEquality,

		// SETTERS
		setSelectConditionType,
		setExtendedSelectConditionType,
		setSelectDepth,
		setSelectOperation,
		setSelectEventId,
		setConfirmationValue,
		setCheckIntervalEquality,

		//  FORM
		formik,

		// HANDLERS
		changeCheckIntervalEquality,
		changeTypeConditionRunHandler,
		changeValuesConditionRunHandler,
		deleteConditionHandler,
		handleChangeSelectDepth,
		handleChangeSelectOperation,

		// OPTIONS
		dataElemOptions,
		timeIntervalsOptions,
		variableNamesOptions,
		variableValuesOptions,
		conditionsTypesList,

		// STORE DATA
		robotInfo,
		dataElemList,
		endpointList,
		endpointElem,

		// HELPERS
		translate,
		dispatch,
	};
};
